import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { BanknoteIcon, CalendarIcon, LockIcon, UserIcon, CreditCardIcon, CheckCircleIcon, XCircleIcon, XIcon, LoaderIcon } from 'lucide-react';
type ProcessingStatus = 'idle' | 'processing' | 'success' | 'error';
export function VirtualTerminal() {
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus>('idle');
  const [reference, setReference] = useState('');
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors placeholder:text-gray-400';
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ').substring(0, 19);
  };
  const formatExpiryDate = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 2) return `${digits.substring(0, 2)}/${digits.substring(2, 4)}`;
    return digits;
  };
  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCardNumber(formatCardNumber(e.target.value));
  };
  const handleExpiryDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setExpiryDate(formatExpiryDate(e.target.value));
  };
  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCvv(e.target.value.replace(/\D/g, '').substring(0, 4));
  };
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^\d.]/g, '');
    if (value.split('.').length > 2) return;
    setAmount(value);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cardNumber || !expiryDate || !cvv || !cardholderName || !amount) return;
    setProcessingStatus('processing');
    const newReference = `REF-${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`;
    setReference(newReference);
    setTimeout(() => {
      setProcessingStatus(Math.random() < 0.8 ? 'success' : 'error');
    }, 2000);
  };
  const resetForm = () => {
    setCardNumber('');
    setExpiryDate('');
    setCvv('');
    setCardholderName('');
    setAmount('');
    setDescription('');
    setProcessingStatus('idle');
    setReference('');
  };
  const closeModal = () => {
    if (processingStatus === 'success') resetForm();else setProcessingStatus('idle');
  };
  const isFormValid = cardNumber && expiryDate && cvv && cardholderName && amount;
  return <div className="max-w-2xl mx-auto" data-id="element-12087">
      <form onSubmit={handleSubmit} className="space-y-5" data-id="element-12088">
        {/* ─── PAYMENT DETAILS ─── */}
        <div className="bg-white rounded-xl border border-gray-200" data-id="element-12089">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-12090">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-12091">
              <BanknoteIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-12092" />
              Payment details
            </h2>
            <p className="text-xs text-gray-500 mt-1" data-id="element-12093">
              Process a Mail Order / Telephone Order transaction.
            </p>
          </div>

          <div className="px-6 py-4 border-b border-gray-200" data-id="element-12094">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-12095">
              Amount <span className="text-red-400" data-id="element-12096">*</span>
            </label>
            <div className="relative" data-id="element-12097">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12098">
                <span className="text-sm font-medium text-gray-500" data-id="element-12099">THB</span>
              </div>
              <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={handleAmountChange} data-id="element-12100" />
            </div>
          </div>

          <div className="px-6 py-4" data-id="element-12101">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-12102">
              Description{' '}
              <span className="normal-case text-gray-400" data-id="element-12103">(optional)</span>
            </label>
            <input type="text" className={inputClass} placeholder="e.g., Product purchase" value={description} onChange={e => setDescription(e.target.value)} data-id="element-12104" />
          </div>
        </div>

        {/* ─── CARD INFORMATION ─── */}
        <div className="bg-white rounded-xl border border-gray-200" data-id="element-12105">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-12106">
            <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-12107">
              <CreditCardIcon size={16} className="mr-2 text-[#F7941D]" data-id="element-12108" />
              Card information
            </h2>
          </div>

          <div className="px-6 py-4 border-b border-gray-200" data-id="element-12109">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-12110">
              Card number <span className="text-red-400" data-id="element-12111">*</span>
            </label>
            <div className="relative" data-id="element-12112">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12113">
                <CreditCardIcon size={14} className="text-gray-400" data-id="element-12114" />
              </div>
              <input type="text" className={`${inputClass} pl-9`} placeholder="XXXX XXXX XXXX XXXX" value={cardNumber} onChange={handleCardNumberChange} data-id="element-12115" />
            </div>
          </div>

          <div className="px-6 py-4 border-b border-gray-200 grid grid-cols-2 gap-4" data-id="element-12116">
            <div data-id="element-12117">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-12118">
                Expiry date <span className="text-red-400" data-id="element-12119">*</span>
              </label>
              <div className="relative" data-id="element-12120">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12121">
                  <CalendarIcon size={14} className="text-gray-400" data-id="element-12122" />
                </div>
                <input type="text" className={`${inputClass} pl-9`} placeholder="MM/YY" value={expiryDate} onChange={handleExpiryDateChange} data-id="element-12123" />
              </div>
            </div>
            <div data-id="element-12124">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-12125">
                CVV / CVC <span className="text-red-400" data-id="element-12126">*</span>
              </label>
              <div className="relative" data-id="element-12127">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12128">
                  <LockIcon size={14} className="text-gray-400" data-id="element-12129" />
                </div>
                <input type="text" className={`${inputClass} pl-9`} placeholder="XXX" value={cvv} onChange={handleCvvChange} data-id="element-12130" />
              </div>
            </div>
          </div>

          <div className="px-6 py-4" data-id="element-12131">
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-12132">
              Cardholder name <span className="text-red-400" data-id="element-12133">*</span>
            </label>
            <div className="relative" data-id="element-12134">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-12135">
                <UserIcon size={14} className="text-gray-400" data-id="element-12136" />
              </div>
              <input type="text" className={`${inputClass} pl-9`} placeholder="Name as it appears on card" value={cardholderName} onChange={e => setCardholderName(e.target.value)} data-id="element-12137" />
            </div>
          </div>
        </div>

        {/* ─── SECURITY NOTE ─── */}
        <div className="flex items-center gap-2 px-1 text-xs text-gray-400" data-id="element-12138">
          <LockIcon size={12} className="flex-shrink-0" data-id="element-12139" />
          <span data-id="element-12140">
            Card information is securely processed and encrypted end-to-end.
          </span>
        </div>

        {/* ─── SUBMIT ─── */}
        <div className="flex justify-end" data-id="element-12141">
          <button type="submit" disabled={!isFormValid} className={`px-6 py-2.5 text-sm font-medium rounded-lg transition-colors shadow-sm ${isFormValid ? 'bg-[#F7941D] hover:bg-[#D97B06] text-white' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`} data-id="element-12142">
            Process payment
          </button>
        </div>
      </form>

      {/* ─── RESULT MODAL ─── */}
      {processingStatus !== 'idle' && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} data-id="element-12143">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-12144">
              {/* Processing */}
              {processingStatus === 'processing' && <div className="p-8 flex flex-col items-center" data-id="element-12145">
                  <div className="w-14 h-14 rounded-full bg-[#F7941D]/10 flex items-center justify-center mb-4" data-id="element-12146">
                    <LoaderIcon size={24} className="text-[#F7941D] animate-spin" data-id="element-12147" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1" data-id="element-12148">
                    Processing payment
                  </h3>
                  <p className="text-sm text-gray-500 text-center" data-id="element-12149">
                    Please wait while we process the transaction…
                  </p>
                </div>}

              {/* Success */}
              {processingStatus === 'success' && <>
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-12150">
                    <h3 className="text-base font-semibold text-gray-900" data-id="element-12151">
                      Payment successful
                    </h3>
                    <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-12152">
                      <XIcon size={18} data-id="element-12153" />
                    </button>
                  </div>
                  <div className="p-6 flex flex-col items-center" data-id="element-12154">
                    <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-4" data-id="element-12155">
                      <CheckCircleIcon size={28} className="text-green-600" data-id="element-12156" />
                    </div>
                    <p className="text-sm text-gray-500 text-center mb-5" data-id="element-12157">
                      The transaction has been approved.
                    </p>

                    {/* Transaction details */}
                    <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden mb-5" data-id="element-12158">
                      <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-200" data-id="element-12159">
                        <span className="text-xs text-gray-500" data-id="element-12160">Amount</span>
                        <span className="text-xs font-medium text-gray-900" data-id="element-12161">
                          THB {parseFloat(amount || '0').toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-200" data-id="element-12162">
                        <span className="text-xs text-gray-500" data-id="element-12163">Reference</span>
                        <span className="text-xs font-medium text-gray-900 font-mono" data-id="element-12164">
                          {reference}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-2.5 border-b border-gray-200" data-id="element-12165">
                        <span className="text-xs text-gray-500" data-id="element-12166">Card</span>
                        <span className="text-xs font-medium text-gray-900" data-id="element-12167">
                          •••• {cardNumber.slice(-4)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center px-4 py-2.5" data-id="element-12168">
                        <span className="text-xs text-gray-500" data-id="element-12169">Date</span>
                        <span className="text-xs font-medium text-gray-900" data-id="element-12170">
                          {new Date().toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <button onClick={closeModal} className="px-5 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12171">
                      New transaction
                    </button>
                  </div>
                </>}

              {/* Error */}
              {processingStatus === 'error' && <>
                  <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-12172">
                    <h3 className="text-base font-semibold text-gray-900" data-id="element-12173">
                      Transaction failed
                    </h3>
                    <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-12174">
                      <XIcon size={18} data-id="element-12175" />
                    </button>
                  </div>
                  <div className="p-6 flex flex-col items-center" data-id="element-12176">
                    <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mb-4" data-id="element-12177">
                      <XCircleIcon size={28} className="text-red-600" data-id="element-12178" />
                    </div>
                    <p className="text-sm text-gray-500 text-center mb-4" data-id="element-12179">
                      The payment could not be processed.
                    </p>

                    <div className="w-full bg-red-50 border border-red-100 rounded-lg p-3 mb-5" data-id="element-12180">
                      <p className="text-xs text-red-700" data-id="element-12181">
                        Transaction declined by issuing bank. Please try a
                        different payment method or contact the card issuer.
                      </p>
                    </div>

                    <button onClick={closeModal} className="px-5 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12182">
                      Try again
                    </button>
                  </div>
                </>}
            </div>
          </div>, document.body)}
    </div>;
}